import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';


/* =========================================================
   MAWJOOD — SMART SEARCH
========================================================= */

const MAWJOOD_SYNONYMS = {

  /* TOYS */
  'العاب': 'pop mart',
  'ألعاب': 'pop mart',
  'لعبة': 'pop mart',
  'لعبه': 'pop mart',
  'لعب': 'pop mart',
  'العاب اطفال': 'pop mart',
  'ألعاب أطفال': 'pop mart',
  'دمية': 'pop mart',
  'دمى': 'pop mart',

  /* SUNGLASSES */
  'نظارة': 'sunglasses',
  'نظاره': 'sunglasses',
  'نظارات': 'sunglasses',
  'نظارات شمسية': 'sunglasses',
  'نظارات شمسيه': 'sunglasses',
  'شمسية': 'sunglasses',
  'شمسيه': 'sunglasses',

  'رجالي': 'men',
  'رجالية': 'men',
  'رجاليه': 'men',

  'نسائي': 'women',
  'نسائية': 'women',
  'نسائيه': 'women',

  'رياضي': 'sport',
  'رياضية': 'sport',
  'رياضيه': 'sport',

  /* TECH */
  'تقنية': 'tech',
  'تقنيه': 'tech',
  'الكترونيات': 'tech',
  'إلكترونيات': 'tech',

  'شاحن': 'charger',
  'شواحن': 'charger',
  'شحن': 'charger',

  'كيبل': 'cable',
  'كابل': 'cable',
  'كابلات': 'cable',
  'كوابل': 'cable',

  'باوربانك': 'power bank',
  'باور بنك': 'power bank',

  'سماعة': 'earphones',
  'سماعه': 'earphones',
  'سماعات': 'earphones',

  /* BEAUTY */
  'تجميل': 'beauty',
  'مكياج': 'makeup',
  'روج': 'lipstick',
  'احمر شفاه': 'lipstick',
  'أحمر شفاه': 'lipstick',

  /* WHOLESALE */
  'جملة': 'wholesale',
  'جمله': 'wholesale',
  'سوق الجملة': 'wholesale',
  'سوق الجمله': 'wholesale',
  'بالجملة': 'wholesale',
  'بالجمله': 'wholesale',

  /* FOOD */
  'قهوة': 'coffee',
  'قهوه': 'coffee',

  'كبسولة': 'capsules',
  'كبسولات': 'capsules',

  'عصير': 'juice',
  'عصائر': 'juice',

  'مشروب': 'drink',
  'مشروبات': 'drink',

  /* SNACKS */
  'سناك': 'chips',
  'سناكات': 'chips',
  'سناكس': 'chips',
  'شيبس': 'chips',
  'بطاطا': 'chips',
  'تسالي': 'chips',

  /* LIGHTERS */
  'ولاعة': 'lighter',
  'ولاعه': 'lighter',
  'ولاعات': 'lighter'
};


/* =========================================================
   NORMALIZE
========================================================= */

function mwNormalizeSearch(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/[\u064B-\u065F\u0670]/g, '')
    .replace(/ـ/g, '')
    .replace(/[أإآ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/\s+/g, ' ');
}


/* =========================================================
   SMART TERM
========================================================= */

function mwSmartSearch(value = '') {

  const original = String(value).trim();

  if (!original) return '';

  const normalized = mwNormalizeSearch(original);

  if (MAWJOOD_SYNONYMS[normalized]) {
    return MAWJOOD_SYNONYMS[normalized];
  }

  let result = normalized;

  const phrases = Object.entries(MAWJOOD_SYNONYMS)
    .filter(([key]) => mwNormalizeSearch(key).includes(' '))
    .sort(([a], [b]) => b.length - a.length);

  for (const [source, replacement] of phrases) {

    const normalizedSource = mwNormalizeSearch(source);

    if (result.includes(normalizedSource)) {
      result = result
        .split(normalizedSource)
        .join(replacement);
    }
  }

  result = result
    .split(' ')
    .map((word) => MAWJOOD_SYNONYMS[word] || word)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  return result || original;
}


/* =========================================================
   COMPONENT
========================================================= */

class PredictiveSearchComponent extends Component {

  requiredRefs = [
    'searchInput',
    'predictiveSearchResults',
    'resetButton'
  ];

  #controller = new AbortController();
  #activeFetch = null;
  #emptyStateLoaded = false;
  #isKeyboardNavigation = false;


  get dialog() {
    return this.closest('dialog-component');
  }


  connectedCallback() {

    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {

      document.addEventListener(
        'keydown',
        this.#handleKeyboardShortcut,
        { signal }
      );

      dialog.addEventListener(
        DialogCloseEvent.eventName,
        this.#handleDialogClose,
        { signal }
      );

      /*
       * IMPORTANT:
       * runs every time search opens.
       */
      dialog.addEventListener(
        DialogOpenEvent.eventName,
        this.#handleDialogOpen,
        { signal }
      );

      this.addEventListener(
        'click',
        this.#handleModalClick,
        { signal }
      );
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }


  disconnectedCallback() {

    super.disconnectedCallback();

    this.#controller.abort();

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
  }


  /* =====================================================
     CLICK
  ===================================================== */

  #handleModalClick = (event) => {

    const target = event.target;

    if (!(target instanceof HTMLElement)) return;

    const interactive =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!interactive && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };


  /* =====================================================
     SHORTCUT
  ===================================================== */

  #handleKeyboardShortcut = (event) => {

    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };


  /* =====================================================
     CLOSE
  ===================================================== */

  #handleDialogClose = () => {

    /*
     * Clear old word after closing search.
     */

    this.refs.searchInput.value = '';

    this.#hideResetButton();

    this.#resetSearch();
  };


  /* =====================================================
     OPEN
  ===================================================== */

  #handleDialogOpen = () => {

    /*
     * Always open with empty search box.
     */

    this.refs.searchInput.value = '';

    this.#hideResetButton();

    this.#resetSearch();

    requestAnimationFrame(() => {
      this.refs.searchInput.focus();
    });
  };


  #loadEmptyState() {

    if (this.#emptyStateLoaded) return;

    this.#emptyStateLoaded = true;

    this.resetSearch(false);
  }


  /* =====================================================
     RESULTS
  ===================================================== */

  get #allResultsItems() {

    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
        '.predictive-search-results__wrapper-products, ' +
        '.predictive-search-results__list'
      )
    );

    return containers
      .flatMap((container) => {

        if (
          container.classList.contains(
            'predictive-search-results__wrapper-products'
          )
        ) {
          return Array.from(
            container.querySelectorAll(
              '.predictive-search-results__card'
            )
          );
        }

        return Array.from(
          container.querySelectorAll(
            '[ref="resultsItems[]"], .predictive-search-results__card'
          )
        );
      })
      .filter((item) => item instanceof HTMLElement);
  }


  get #currentIndex() {

    return this.#allResultsItems.findIndex(
      (item) =>
        item.getAttribute('aria-selected') === 'true'
    );
  }


  set #currentIndex(index) {

    const items = this.#allResultsItems;

    if (!items.length) return;

    let activeItem = null;

    items.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of items.entries()) {

      if (itemIndex === index) {

        item.setAttribute(
          'aria-selected',
          'true'
        );

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }

        activeItem = item;

      } else {

        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({
      behavior:
        prefersReducedMotion()
          ? 'instant'
          : 'smooth',
      block: 'nearest'
    });

    this.refs.searchInput.focus();
  }


  get #currentItem() {
    return this.#allResultsItems[this.#currentIndex];
  }


  /* =====================================================
     KEYBOARD
  ===================================================== */

  onSearchKeyDown = (event) => {

    if (event.key === 'Escape') {

      this.#resetSearch();

      return;
    }

    const items = this.#allResultsItems;

    if (
      !items.length ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight'
    ) {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = items.length;

    switch (event.key) {

      case 'ArrowDown':

        this.#isKeyboardNavigation = true;

        event.preventDefault();

        this.#currentIndex =
          currentIndex < totalItems - 1
            ? currentIndex + 1
            : 0;

        break;


      case 'ArrowUp':

        this.#isKeyboardNavigation = true;

        event.preventDefault();

        this.#currentIndex =
          currentIndex > 0
            ? currentIndex - 1
            : totalItems - 1;

        break;


      case 'Tab':

        this.#isKeyboardNavigation = true;

        event.preventDefault();

        if (event.shiftKey) {

          this.#currentIndex =
            currentIndex > 0
              ? currentIndex - 1
              : totalItems - 1;

        } else {

          this.#currentIndex =
            currentIndex < totalItems - 1
              ? currentIndex + 1
              : 0;
        }

        break;


      case 'Enter': {

        const singleResultContainer =
          this.refs
            .predictiveSearchResults
            .querySelector(
              '[data-single-result-url]'
            );

        if (
          singleResultContainer instanceof HTMLElement &&
          singleResultContainer.dataset.singleResultUrl
        ) {

          event.preventDefault();

          /*
           * Clear before navigation.
           */

          this.refs.searchInput.value = '';
          this.#hideResetButton();

          window.location.href =
            singleResultContainer.dataset.singleResultUrl;

          return;
        }


        if (this.#currentIndex >= 0) {

          event.preventDefault();

          const selectedLink =
            this.#currentItem?.querySelector('a');

          /*
           * Clear before opening product.
           */

          this.refs.searchInput.value = '';
          this.#hideResetButton();

          selectedLink?.click();

          return;
        }


        const rawTerm =
          this.refs.searchInput.value.trim();

        if (!rawTerm) return;

        event.preventDefault();

        /*
         * Full /search keeps original Arabic/English term.
         */

        const searchUrl =
          new URL(
            Theme.routes.search_url,
            location.origin
          );

        searchUrl.searchParams.set(
          'q',
          rawTerm
        );

        /*
         * Clear UI before navigation.
         */

        this.refs.searchInput.value = '';
        this.#hideResetButton();

        window.location.href =
          searchUrl.toString();

        break;
      }
    }
  };


  /* =====================================================
     RECENTLY VIEWED
  ===================================================== */

  clearRecentlyViewedProducts(event) {

    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const {
      recentlyViewedItems,
      recentlyViewedTitle,
      recentlyViewedWrapper
    } = this.refs;

    const elements = [
      ...(recentlyViewedItems || []),
      ...(recentlyViewedTitle || [])
    ];

    if (elements.length === 0) return;

    if (recentlyViewedWrapper) {

      recentlyViewedWrapper.classList.add(
        'removing'
      );

      onAnimationEnd(
        recentlyViewedWrapper,
        () => {
          recentlyViewedWrapper.remove();
        }
      );
    }
  }


  resetSearch = debounce(
    (keepFocus = true) => {

      if (keepFocus) {
        this.refs.searchInput.focus();
      }

      this.#resetSearch();

    },
    100
  );


  /* =====================================================
     LIVE SEARCH
  ===================================================== */

  search = debounce(
    (event) => {

      if (!event.inputType) return;

      const rawSearchTerm =
        this.refs.searchInput.value.trim();

      this.#currentIndex = -1;

      if (!rawSearchTerm.length) {

        this.#resetSearch();

        return;
      }

      this.#showResetButton();

      const smartSearchTerm =
        mwSmartSearch(rawSearchTerm);

      this.#getSearchResults(
        smartSearchTerm
      );

    },
    220
  );


  #resetScrollPositions() {

    requestAnimationFrame(() => {

      this.refs
        .predictiveSearchResults
        .querySelector(
          '.predictive-search-results__inner'
        )
        ?.scrollTo(0, 0);

      this.querySelector(
        '.predictive-search-form__content'
      )
        ?.scrollTo(0, 0);
    });
  }


  /* =====================================================
     SHOPIFY SEARCH
  ===================================================== */

  async #getSearchResults(searchTerm) {

    if (!this.dataset.sectionId) return;

    const url =
      new URL(
        Theme.routes.predictive_search_url,
        location.origin
      );

    url.searchParams.set(
      'q',
      searchTerm
    );

    url.searchParams.set(
      'resources[type]',
      'product,collection,query'
    );

    url.searchParams.set(
      'resources[limit]',
      '10'
    );

    url.searchParams.set(
      'resources[limit_scope]',
      'each'
    );

    url.searchParams.set(
      'resources[options][fields]',
      [
        'title',
        'product_type',
        'vendor',
        'tag',
        'body',
        'variants.title',
        'variants.sku',
        'variants.barcode'
      ].join(',')
    );

    url.searchParams.set(
      'resources[options][unavailable_products]',
      'last'
    );

    const {
      predictiveSearchResults
    } = this.refs;

    const abortController =
      this.#createAbortController();

    const deferredPromise =
      SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm
        },
        promise:
          deferredPromise.promise
      })
    );

    try {

      const resultsMarkup =
        await sectionRenderer.getSectionHTML(
          this.dataset.sectionId,
          false,
          url
        );

      if (!resultsMarkup) {

        deferredPromise.resolve({
          totalCount: 0
        });

        return;
      }

      if (
        abortController.signal.aborted
      ) {

        deferredPromise.reject(
          new Error(
            'Fetch aborted by user'
          )
        );

        return;
      }

      morph(
        predictiveSearchResults,
        resultsMarkup
      );

      this.#resetScrollPositions();

      const resultCount =
        predictiveSearchResults
          .querySelectorAll(
            '[ref="resultsItems[]"]'
          )
          .length;

      deferredPromise.resolve({
        totalCount: resultCount
      });

    } catch (error) {

      deferredPromise.reject(error);

      if (
        abortController.signal.aborted
      ) {
        return;
      }

      console.error(
        'MAWJOOD predictive search:',
        error
      );
    }
  }


  /* =====================================================
     RECENTLY VIEWED MARKUP
  ===================================================== */

  async #getRecentlyViewedProductsMarkup() {

    if (!this.dataset.sectionId) {
      return null;
    }

    const viewedProducts =
      RecentlyViewed.getProducts();

    if (
      viewedProducts.length === 0
    ) {
      return null;
    }

    const url =
      new URL(
        Theme.routes.search_url,
        location.origin
      );

    url.searchParams.set(
      'q',
      viewedProducts
        .map((id) => `id:${id}`)
        .join(' OR ')
    );

    url.searchParams.set(
      'resources[type]',
      'product'
    );

    return sectionRenderer.getSectionHTML(
      this.dataset.sectionId,
      false,
      url
    );
  }


  /* =====================================================
     RESET BUTTON
  ===================================================== */

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }


  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }


  /* =====================================================
     ABORT
  ===================================================== */

  #createAbortController() {

    const abortController =
      new AbortController();

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    this.#activeFetch =
      abortController;

    return abortController;
  }


  /* =====================================================
     RESET SEARCH
  ===================================================== */

  #resetSearch = async () => {

    const {
      predictiveSearchResults,
      searchInput
    } = this.refs;

    const emptySectionId =
      'predictive-search-empty';

    this.#currentIndex = -1;

    searchInput.value = '';

    this.#hideResetButton();

    const abortController =
      this.#createAbortController();

    const url =
      new URL(
        window.location.href
      );

    url.searchParams.delete('page');

    try {

      const emptySectionMarkup =
        await sectionRenderer.getSectionHTML(
          emptySectionId,
          false,
          url
        );

      const parsedEmptySectionMarkup =
        new DOMParser()
          .parseFromString(
            emptySectionMarkup,
            'text/html'
          )
          .querySelector(
            '.predictive-search-empty-section'
          );

      if (!parsedEmptySectionMarkup) {
        return;
      }

      const viewedProducts =
        RecentlyViewed.getProducts();

      if (
        viewedProducts.length > 0
      ) {

        const recentlyViewedMarkup =
          await this
            .#getRecentlyViewedProductsMarkup();

        if (recentlyViewedMarkup) {

          const parsedRecentlyViewedMarkup =
            new DOMParser()
              .parseFromString(
                recentlyViewedMarkup,
                'text/html'
              );

          const recentlyViewedProductsHtml =
            parsedRecentlyViewedMarkup
              .getElementById(
                'predictive-search-products'
              );

          if (recentlyViewedProductsHtml) {

            for (
              const child of
              recentlyViewedProductsHtml.children
            ) {

              if (
                child instanceof HTMLElement
              ) {
                child.setAttribute(
                  'ref',
                  'recentlyViewedWrapper'
                );
              }
            }

            const collectionElement =
              parsedEmptySectionMarkup
                .querySelector(
                  '#predictive-search-products'
                );

            if (collectionElement) {

              collectionElement.prepend(
                ...recentlyViewedProductsHtml.children
              );
            }
          }
        }
      }

      if (
        abortController.signal.aborted
      ) {
        return;
      }

      morph(
        predictiveSearchResults,
        parsedEmptySectionMarkup
      );

      this.#resetScrollPositions();

    } catch (error) {

      if (
        abortController.signal.aborted
      ) {
        return;
      }

      console.error(
        'MAWJOOD reset search:',
        error
      );
    }
  };
}


/* =========================================================
   REGISTER
========================================================= */

if (
  !customElements.get(
    'predictive-search-component'
  )
) {

  customElements.define(
    'predictive-search-component',
    PredictiveSearchComponent
  );
}